// ツールごとの短いガイド。ここが唯一の定義で、次の 3 か所が同じ文言を使う:
//   1. オプションバーの 1 行ヒント (常時、控えめ)
//   2. ツールガイドのポップオーバー (F1 / ? ボタン、必要なときだけ)
//   3. そのツールを初めて選んだときのトースト (1 回だけ)
// 説明文は UI ラベルとは性質が違うので i18n 辞書には入れず、ja/en を並べてここに持つ。

import type { ToolId } from '@/stores/uiStore'
import { locale } from '@/i18n'

/** 日英の対訳 */
export interface L {
  ja: string
  en: string
}

export interface ToolGuide {
  /** 1 行の要約 (常時表示するのはこれだけ) */
  summary: L
  /** 手順 */
  steps: L[]
  /** キー操作 / 修飾キー */
  keys?: { key: string; desc: L }[]
  /** 知っていると得をすること */
  tip?: L
}

export function tx(v: L): string {
  return locale.value === 'en' ? v.en : v.ja
}

export const TOOL_GUIDES: Record<ToolId, ToolGuide> = {
  move: {
    summary: { ja: 'ドラッグで選択中のレイヤーを動かします', en: 'Drag to move the selected layer.' },
    steps: [
      { ja: 'レイヤーパネルで動かすレイヤーを選ぶ', en: 'Select a layer in the Layers panel' },
      { ja: 'カンバスをドラッグする', en: 'Drag on the canvas' },
      { ja: '複数選択中はオプションバーの整列 / 分布ボタンが使える', en: 'With several layers selected, use the align / distribute buttons in the options bar' }
    ],
    keys: [{ key: 'Shift / Ctrl', desc: { ja: 'レイヤーパネルで複数選択', en: 'Select multiple layers in the Layers panel' } }],
    tip: { ja: '表示メニューのスナップが ON なら、ガイド・グリッド・カンバス端に吸着します。定規からドラッグして作ったガイドも、このツールで動かせます', en: 'With snapping on (View menu), layers stick to guides, the grid and canvas edges. Guides dragged out of the rulers are also moved with this tool.' }
  },
  marquee: {
    summary: { ja: 'ドラッグで長方形 / 楕円の選択範囲を作ります', en: 'Drag to make a rectangular or elliptical selection.' },
    steps: [
      { ja: 'オプションバーで形状 (長方形 / 楕円) を選ぶ', en: 'Pick the shape (rectangle / ellipse) in the options bar' },
      { ja: 'カンバスをドラッグする', en: 'Drag on the canvas' },
      { ja: 'Ctrl+D で選択を解除する', en: 'Press Ctrl+D to deselect' }
    ],
    keys: [
      { key: 'Shift', desc: { ja: '選択範囲に追加', en: 'Add to the selection' } },
      { key: 'Alt', desc: { ja: '選択範囲から削除', en: 'Subtract from the selection' } },
      { key: 'Shift + Alt', desc: { ja: '共通部分だけ残す', en: 'Intersect with the selection' } }
    ],
    tip: { ja: 'オプションバーの「ぼかし」を 0 より大きくすると縁が柔らかくなります', en: 'Set Feather above 0 in the options bar for a soft edge.' }
  },
  lasso: {
    summary: { ja: 'フリーハンド / 多角形 / マグネットで選択範囲を作ります', en: 'Draw a freehand, polygonal or magnetic selection.' },
    steps: [
      { ja: 'オプションバーで種類を選ぶ', en: 'Choose the type in the options bar' },
      { ja: 'なげなわ: 囲むようにドラッグする', en: 'Freehand: drag around the area' },
      { ja: '多角形: クリックで頂点を置き、始点をクリックか Enter で閉じる', en: 'Polygonal: click to add points, click the first point or press Enter to close' },
      { ja: 'マグネット: 輪郭に沿って動かすと境界に吸着する。Enter で閉じる', en: 'Magnetic: move along an edge and the path snaps to it. Press Enter to close' }
    ],
    keys: [
      { key: 'Shift / Alt', desc: { ja: '選択範囲に追加 / から削除', en: 'Add to / subtract from the selection' } },
      { key: 'Backspace', desc: { ja: '直前の点を取り消す (多角形 / マグネット)', en: 'Undo the last point (polygonal / magnetic)' } },
      { key: 'Esc', desc: { ja: '中止する', en: 'Cancel' } }
    ]
  },
  wand: {
    summary: { ja: 'クリックした場所と似た色をまとめて選択します', en: 'Click to select areas of similar color.' },
    steps: [
      { ja: 'オプションバーで許容値を決める (大きいほど広く選ぶ)', en: 'Set the tolerance in the options bar (higher selects more)' },
      { ja: '選びたい色をクリックする', en: 'Click the color you want' },
      { ja: '足りなければ Shift+クリックで足す', en: 'Shift-click to add more' }
    ],
    keys: [{ key: 'Shift / Alt', desc: { ja: '追加 / 削除', en: 'Add / subtract' } }],
    tip: { ja: '「隣接」を外すと画像全体の同じ色を選びます。W をもう一度押すとクイック選択に切り替わります', en: 'Turn off Contiguous to select matching colors anywhere. Press W again to switch to Quick Selection.' }
  },
  quickSelect: {
    summary: { ja: '撫でた場所から、似た色の領域へ自動で広がります', en: 'Paint roughly and the selection grows to similar areas.' },
    steps: [
      { ja: 'ブラシのサイズを決める', en: 'Set the brush size' },
      { ja: '選びたい被写体の内側をドラッグする', en: 'Drag inside the subject you want' },
      { ja: 'はみ出したら Alt+ドラッグで削る', en: 'Alt-drag to subtract where it spills over' }
    ],
    keys: [{ key: 'Alt', desc: { ja: '選択範囲から削る', en: 'Subtract from the selection' } }],
    tip: { ja: '輪郭がはっきりしているほどうまく止まります。仕上げは 選択範囲 → 選択範囲を調整 でぼかしや収縮を', en: 'It stops better on clear edges. Finish with Select → Refine Edge to feather or shift the border.' }
  },
  crop: {
    summary: { ja: 'ドラッグした範囲でカンバスを切り抜きます', en: 'Drag to crop the canvas.' },
    steps: [
      { ja: '残したい範囲をドラッグする', en: 'Drag the area you want to keep' },
      { ja: '角をドラッグで調整、内側をドラッグで移動', en: 'Drag the handles to resize, drag inside to move' },
      { ja: 'Enter で確定する', en: 'Press Enter to apply' }
    ],
    keys: [
      { key: 'Enter', desc: { ja: '確定', en: 'Apply' } },
      { key: 'Esc', desc: { ja: '取り消し', en: 'Cancel' } }
    ],
    tip: { ja: '選択範囲があると、その外接矩形が初期値になります', en: 'If there is a selection, its bounds are used as the starting crop.' }
  },
  eyedropper: {
    summary: { ja: 'クリックした色を描画色にします', en: 'Click to pick up a color as the foreground color.' },
    steps: [
      { ja: '拾いたい色をクリックする', en: 'Click the color you want' },
      { ja: 'Alt+クリックで背景色にする', en: 'Alt-click to set the background color instead' }
    ],
    keys: [{ key: 'Alt', desc: { ja: '背景色として拾う', en: 'Pick as background color' } }],
    tip: { ja: '合成結果 (重なった見た目) から拾います。拾った色はヒストグラム / 情報パネルにも出ます', en: 'It samples the flattened composite. The value also shows in the Histogram / Info panel.' }
  },
  brush: {
    summary: { ja: 'ドラッグして描画色で描きます', en: 'Drag to paint with the foreground color.' },
    steps: [
      { ja: 'ラスターレイヤーを選ぶ (テキストやシェイプには描けません)', en: 'Select a raster layer (you cannot paint on text or shapes)' },
      { ja: 'サイズと硬さを決める', en: 'Set size and hardness' },
      { ja: 'カンバスをドラッグする', en: 'Drag on the canvas' }
    ],
    keys: [
      { key: '[ / ]', desc: { ja: 'ブラシを小さく / 大きく', en: 'Decrease / increase brush size' } },
      { key: 'Alt', desc: { ja: '背景色で描く', en: 'Paint with the background color' } },
      { key: 'X', desc: { ja: '描画色と背景色を入れ替える', en: 'Swap foreground and background' } }
    ],
    tip: { ja: '不透明度は 1 ストロークの上限、流量は重ねるほど濃くなる量です。編集 → ブラシを定義 で画像から先端を作れます', en: 'Opacity caps a whole stroke; Flow is how much each dab adds. Edit → Define Brush Preset makes a tip from your image.' }
  },
  spotHeal: {
    summary: { ja: '撫でた場所を、周囲の模様で自動的に補います', en: 'Paint over a blemish and it is filled from the surroundings.' },
    steps: [
      { ja: '消したい点や傷の上を撫でる', en: 'Paint over the spot or scratch' },
      { ja: '手を離すと周囲から似た場所を探して馴染ませます', en: 'On release it finds a similar patch nearby and blends it in' }
    ],
    tip: { ja: '広い範囲は、選択してから 編集 → コンテンツに応じた塗りつぶし が向いています。J をもう一度押すと修復ブラシに切り替わります', en: 'For large areas, select them and use Edit → Content-Aware Fill. Press J again for the Healing Brush.' }
  },
  heal: {
    summary: { ja: 'Alt+クリックで決めたソースを写し、周囲の明るさに馴染ませます', en: 'Alt-click a source, then paint: the pixels are blended into their new surroundings.' },
    steps: [
      { ja: 'Alt+クリックでコピー元を指定する', en: 'Alt-click to set the source' },
      { ja: '直したい場所をドラッグする', en: 'Drag over the area to repair' }
    ],
    keys: [{ key: 'Alt + クリック', desc: { ja: 'コピー元を指定', en: 'Set the source point' } }],
    tip: { ja: '明るさや色が違う場所へ写しても境界が馴染みます。そのまま写したいときはコピースタンプを使います', en: 'The edges blend even across different brightness. Use the Clone Stamp when you want an exact copy.' }
  },
  clone: {
    summary: { ja: 'Alt+クリックで決めた場所の画素を、そのまま複製します', en: 'Alt-click a source, then paint to copy those pixels exactly.' },
    steps: [
      { ja: 'Alt+クリックでコピー元を指定する', en: 'Alt-click to set the source' },
      { ja: '貼りたい場所をドラッグする', en: 'Drag where you want the copy' }
    ],
    keys: [{ key: 'Alt + クリック', desc: { ja: 'コピー元を指定', en: 'Set the source point' } }],
    tip: { ja: '「調整」を外すと、ストロークのたびに同じコピー元から始まります', en: 'Turn off Aligned to restart from the same source on every stroke.' }
  },
  eraser: {
    summary: { ja: 'ドラッグした場所を透明にします', en: 'Drag to erase to transparency.' },
    steps: [
      { ja: 'ラスターレイヤーを選ぶ', en: 'Select a raster layer' },
      { ja: '消したい場所をドラッグする', en: 'Drag over what you want to erase' }
    ],
    tip: { ja: 'あとから戻せるようにするなら、消さずにレイヤーマスク (レイヤー → レイヤーマスクを追加) で隠すのが安全です', en: 'To keep it reversible, hide pixels with a layer mask (Layer → Add Layer Mask) instead of erasing.' }
  },
  blurTool: {
    summary: { ja: 'ドラッグした場所をぼかします', en: 'Drag to blur.' },
    steps: [
      { ja: '強さを決める', en: 'Set the strength' },
      { ja: 'ぼかしたい場所をドラッグする', en: 'Drag over the area to blur' }
    ],
    keys: [{ key: 'R', desc: { ja: 'ぼかし → シャープ → 指先 と切り替え', en: 'Cycle Blur → Sharpen → Smudge' } }],
    tip: { ja: '画像全体をぼかすなら フィルター → ぼかし (ガウス) のほうが速くて綺麗です', en: 'To blur the whole image, Filter → Gaussian Blur is faster and cleaner.' }
  },
  sharpenTool: {
    summary: { ja: 'ドラッグした場所をくっきりさせます', en: 'Drag to sharpen.' },
    steps: [
      { ja: '強さを決める (かけすぎるとざらつきます)', en: 'Set the strength (too much looks noisy)' },
      { ja: '目立たせたい場所をドラッグする', en: 'Drag over the area to sharpen' }
    ],
    keys: [{ key: 'R', desc: { ja: 'ぼかし → シャープ → 指先 と切り替え', en: 'Cycle Blur → Sharpen → Smudge' } }]
  },
  smudge: {
    summary: { ja: 'ドラッグして、色を指でこすったように伸ばします', en: 'Drag to smear colors like a finger.' },
    steps: [
      { ja: '強さを決める (高いほど遠くまで伸びます)', en: 'Set the strength (higher smears further)' },
      { ja: '伸ばしたい向きへドラッグする', en: 'Drag in the direction you want to smear' }
    ],
    keys: [{ key: 'R', desc: { ja: 'ぼかし → シャープ → 指先 と切り替え', en: 'Cycle Blur → Sharpen → Smudge' } }],
    tip: { ja: '手を離してから計算します (ドラッグ中はプレビューが出ません)', en: 'It is computed when you release the pointer, so there is no live preview.' }
  },
  dodge: {
    summary: { ja: 'ドラッグした場所を明るくします', en: 'Drag to lighten.' },
    steps: [
      { ja: '露光量を決める', en: 'Set the exposure' },
      { ja: '明るくしたい場所をドラッグする', en: 'Drag over the area to lighten' }
    ],
    keys: [{ key: 'O', desc: { ja: '覆い焼き → 焼き込み → スポンジ と切り替え', en: 'Cycle Dodge → Burn → Sponge' } }]
  },
  burn: {
    summary: { ja: 'ドラッグした場所を暗くします', en: 'Drag to darken.' },
    steps: [
      { ja: '露光量を決める', en: 'Set the exposure' },
      { ja: '暗くしたい場所をドラッグする', en: 'Drag over the area to darken' }
    ],
    keys: [{ key: 'O', desc: { ja: '覆い焼き → 焼き込み → スポンジ と切り替え', en: 'Cycle Dodge → Burn → Sponge' } }]
  },
  sponge: {
    summary: { ja: 'ドラッグした場所の彩度を上げ下げします', en: 'Drag to saturate or desaturate.' },
    steps: [
      { ja: 'オプションバーで「彩度を下げる / 上げる」を選ぶ', en: 'Choose Desaturate or Saturate in the options bar' },
      { ja: '変えたい場所をドラッグする', en: 'Drag over the area' }
    ],
    keys: [{ key: 'O', desc: { ja: '覆い焼き → 焼き込み → スポンジ と切り替え', en: 'Cycle Dodge → Burn → Sponge' } }]
  },
  fill: {
    summary: { ja: 'クリックした場所と似た色を、描画色で塗ります', en: 'Click to flood-fill similar colors with the foreground color.' },
    steps: [
      { ja: '許容値を決める', en: 'Set the tolerance' },
      { ja: '塗りたい場所をクリックする', en: 'Click the area to fill' }
    ],
    keys: [{ key: 'G', desc: { ja: '塗りつぶし → グラデーション と切り替え', en: 'Cycle Paint Bucket → Gradient' } }],
    tip: { ja: '選択範囲があるとその中だけ塗ります。範囲全体を一気に塗るなら 編集 → 塗りつぶし (Shift+F5)', en: 'It stays inside the selection. To fill the whole selection at once use Edit → Fill (Shift+F5).' }
  },
  gradient: {
    summary: { ja: 'ドラッグした方向にグラデーションを描きます', en: 'Drag to draw a gradient.' },
    steps: [
      { ja: '種類 (線形 / 円形 / 円錐 / 反射 / 菱形) を選ぶ', en: 'Choose the style (linear / radial / angle / reflected / diamond)' },
      { ja: '始点から終点へドラッグする', en: 'Drag from the start point to the end point' }
    ],
    keys: [
      { key: 'Shift', desc: { ja: '45° 単位に固定', en: 'Constrain to 45° steps' } },
      { key: 'G', desc: { ja: '塗りつぶし → グラデーション と切り替え', en: 'Cycle Paint Bucket → Gradient' } }
    ],
    tip: { ja: '既定は描画色 → 背景色です。「カスタム」を ON にするとバー上のストップで色と位置を編集できます', en: 'By default it goes foreground → background. Turn on Custom to edit stops directly on the bar.' }
  },
  text: {
    summary: { ja: 'クリックして文字を入力します', en: 'Click and type.' },
    steps: [
      { ja: 'カンバスをクリックする (既存の文字をクリックすると編集)', en: 'Click the canvas (click existing text to edit it)' },
      { ja: '文字を入力する', en: 'Type your text' },
      { ja: 'Esc か Ctrl+Enter で確定する', en: 'Press Esc or Ctrl+Enter to commit' }
    ],
    keys: [{ key: 'Esc / Ctrl+Enter', desc: { ja: '確定', en: 'Commit the text' } }],
    tip: { ja: '確定後もプロパティパネルでフォント・行間・縦書き・ワープを変えられます (文字はベクターのまま保持されます)', en: 'After committing you can still change font, leading, vertical writing and warp in the Properties panel.' }
  },
  shape: {
    summary: { ja: 'ドラッグして図形 (長方形 / 楕円 / 多角形 / ライン) を作ります', en: 'Drag to draw a rectangle, ellipse, polygon or line.' },
    steps: [
      { ja: 'オプションバーで種類と塗り / 線を決める', en: 'Choose the shape and fill / stroke in the options bar' },
      { ja: 'カンバスをドラッグする', en: 'Drag on the canvas' }
    ],
    keys: [{ key: 'Shift', desc: { ja: '正方形 / 正円にする', en: 'Constrain to a square / circle' } }],
    tip: { ja: 'ベクターなので、拡大しても輪郭が保たれます。画素にするには レイヤー → シェイプをラスタライズ', en: 'Shapes stay vector, so they keep crisp edges when scaled. Use Layer → Rasterize Shape to convert to pixels.' }
  },
  pen: {
    summary: { ja: 'クリックでアンカーを打ち、自由なパスを作ります', en: 'Click to place anchors and draw a path.' },
    steps: [
      { ja: 'クリックで直線、ドラッグで曲線のアンカーを置く', en: 'Click for corners, drag for curves' },
      { ja: '始点をクリックすると閉じたパスになる', en: 'Click the first anchor to close the path' },
      { ja: 'Enter で確定する', en: 'Press Enter to finish' }
    ],
    keys: [
      { key: 'Enter', desc: { ja: '確定', en: 'Finish the path' } },
      { key: 'Esc', desc: { ja: '取り消し', en: 'Cancel' } },
      { key: 'Backspace', desc: { ja: '直前のアンカーを削除', en: 'Remove the last anchor' } },
      { key: 'Alt + ドラッグ', desc: { ja: 'ハンドルを片側だけ動かす (折れ点にする)', en: 'Move one handle only (make a corner)' } }
    ],
    tip: { ja: 'できたパスは レイヤー → パスの境界線をブラシで描く / パスを塗りつぶし で使えます。既存のパスはアンカーをドラッグして編集できます', en: 'Use Layer → Stroke Path / Fill Path afterwards. Drag the anchors of an existing path to edit it.' }
  },
  hand: {
    summary: { ja: 'ドラッグして表示位置を動かします', en: 'Drag to pan the view.' },
    steps: [{ ja: 'カンバスをドラッグする', en: 'Drag on the canvas' }],
    keys: [{ key: 'Space', desc: { ja: '押している間だけ一時的に手のひらになる', en: 'Hold for a temporary Hand tool' } }],
    tip: { ja: 'どのツールを使っていても Space を押している間は手のひらになります。ホイールでも表示を動かせます', en: 'Holding Space works with any tool. The scroll wheel also pans.' }
  },
  zoom: {
    summary: { ja: 'クリックで拡大、Alt+クリックで縮小します', en: 'Click to zoom in, Alt-click to zoom out.' },
    steps: [{ ja: '拡大したい場所をクリックする', en: 'Click where you want to zoom in' }],
    keys: [
      { key: 'Alt', desc: { ja: '縮小', en: 'Zoom out' } },
      { key: 'Ctrl+0 / Ctrl+1', desc: { ja: '全体表示 / 100% 表示', en: 'Fit on screen / actual pixels' } },
      { key: 'Ctrl + ホイール', desc: { ja: '拡大縮小 (トラックパッドはピンチ)', en: 'Zoom (pinch on a trackpad)' } }
    ]
  }
}

export function guideFor(tool: ToolId): ToolGuide {
  return TOOL_GUIDES[tool]
}
